"use client";

import { cn } from "@/lib/utils";

// SVG pattern for decorative backgrounds
const plusPattern = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

const dotsPattern = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1.5'/%3E%3Ccircle cx='13' cy='13' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`;

export type GradientVariant =
  | "emerald"
  | "green-blue"
  | "purple"
  | "amber"
  | "rose"
  | "blue"
  | "teal"
  | "rainbow"
  | "custom";
export type PatternVariant = "none" | "plus" | "dots";

export interface GradientSurfaceProps {
  /** The gradient color scheme to use */
  variant: GradientVariant;
  /** Custom gradient CSS value when variant is 'custom' */
  customGradient?: string;
  /** Optional decorative pattern overlay */
  pattern?: PatternVariant;
  /** Pattern opacity (0-100) */
  patternOpacity?: number;
  /** Additional CSS classes */
  className?: string;
  /** Child elements */
  children?: React.ReactNode;
}

const gradientClasses: Record<Exclude<GradientVariant, "custom">, string> = {
  emerald: "bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700",
  "green-blue": "bg-gradient-to-br from-green-600 via-emerald-600 to-blue-700",
  purple: "bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-600",
  amber: "bg-gradient-to-br from-amber-500 via-orange-500 to-red-500",
  rose: "bg-gradient-to-br from-rose-500 via-pink-500 to-fuchsia-500",
  blue: "bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700",
  teal: "bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-700",
  rainbow: "bg-gradient-to-br from-emerald-400 via-blue-500 to-purple-600",
};

/**
 * GradientSurface - A composable gradient background primitive
 *
 * Use this component to add consistent gradient backgrounds across the app.
 * Supports multiple color variants and optional decorative patterns.
 *
 * @example
 * <GradientSurface variant="emerald" pattern="plus">
 *   <h1>Content on gradient</h1>
 * </GradientSurface>
 */
export function GradientSurface({
  variant,
  customGradient,
  pattern = "none",
  patternOpacity = 10,
  className,
  children,
}: GradientSurfaceProps) {
  const gradientStyle =
    variant === "custom" && customGradient
      ? { background: customGradient }
      : undefined;

  const patternStyle =
    pattern !== "none"
      ? {
          backgroundImage: pattern === "plus" ? plusPattern : dotsPattern,
          opacity: patternOpacity / 100,
        }
      : undefined;

  return (
    <div
      className={cn(
        "relative",
        variant !== "custom" && gradientClasses[variant],
        className
      )}
      style={gradientStyle}
    >
      {/* Pattern overlay */}
      {pattern !== "none" && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={patternStyle}
          aria-hidden="true"
        />
      )}

      {/* Content */}
      {children}
    </div>
  );
}

export default GradientSurface;
